import { styled } from '@mui/material/styles';
import { Collapse, ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import FilterList from '@mui/icons-material/FilterList';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useState } from 'react';
import {
  FILTER_BY_LABELS,
  FilterByOption,
} from '~/modules/matters/useMattersList';

export interface ExpertOption {
  id: string;
  name: string;
}

interface Props {
  expertFilterId: string | null;
  filterBy: FilterByOption;
  expertOptions: ExpertOption[];
  onExpertChange: (expertId: string | null) => void;
  onFilterByChange: (value: FilterByOption) => void;
}

const AttorneyMattersFilter = ({
  expertFilterId,
  filterBy,
  expertOptions,
  onExpertChange,
  onFilterByChange,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expertsOpen, setExpertsOpen] = useState(false);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setExpertsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setAnchorEl(e.currentTarget as unknown as HTMLElement);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setExpertsOpen(false);
  };

  const handleExpertsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpertsOpen((open) => !open);
  };

  const handleExpertSelect = (id: string) => {
    onExpertChange(id);
    setAnchorEl(null);
    setExpertsOpen(false);
  };

  const handleShowAllExperts = () => {
    onExpertChange(null);
    setAnchorEl(null);
    setExpertsOpen(false);
  };

  const handleFilterBySelect = (value: FilterByOption) => {
    onFilterByChange(value);
    setAnchorEl(null);
    setExpertsOpen(false);
  };

  const selectedExpertName =
    expertFilterId !== null
      ? expertOptions.find((opt) => opt.id === expertFilterId)?.name
      : null;
  const valueLabel = selectedExpertName ?? FILTER_BY_LABELS[filterBy];

  return (
    <>
      <Trigger
        aria-label="Filter matters"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={handleOpen}
      >
        <FilterList fontSize="small" color="action" aria-hidden />
        <FilterLabel>Filter: {valueLabel}</FilterLabel>
        <KeyboardArrowDown fontSize="small" color="action" aria-hidden />
      </Trigger>
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          key={FilterByOption.All}
          onClick={() => {
            onExpertChange(null);
            onFilterByChange(FilterByOption.All);
            setAnchorEl(null);
            setExpertsOpen(false);
          }}
        >
          <ListItemText primary={FILTER_BY_LABELS[FilterByOption.All]} />
        </MenuItem>
        <MenuItem onClick={handleExpertsClick} selected={expertsOpen}>
          <ListItemText primary="Experts" />
          <StyledKeyboardArrowRight
            fontSize="small"
            color="action"
            $expanded={expertsOpen}
            aria-hidden
          />
        </MenuItem>
        <Collapse in={expertsOpen} timeout="auto" unmountOnExit>
          <StyledMenuItem onClick={handleShowAllExperts}>
            <ListItemText primary="Show all" />
          </StyledMenuItem>
          {expertOptions.map(({ id, name }) => (
            <StyledMenuItem key={id} onClick={() => handleExpertSelect(id)}>
              <ListItemText primary={name} />
            </StyledMenuItem>
          ))}
        </Collapse>
        {(Object.keys(FILTER_BY_LABELS) as FilterByOption[])
          .filter((key) => key !== FilterByOption.All)
          .map((key) => (
            <MenuItem key={key} onClick={() => handleFilterBySelect(key)}>
              <ListItemText primary={FILTER_BY_LABELS[key]} />
            </MenuItem>
          ))}
      </StyledMenu>
    </>
  );
};

const FilterLabel = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  fontWeight: theme.tokens.filterLabelFontWeight,
}));

const Trigger = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor:
    theme.palette.mattersDropdown?.trigger ?? 'rgba(0, 0, 0, 0.02)',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor:
      theme.palette.mattersDropdown?.triggerHover ?? 'rgba(0, 0, 0, 0.05)',
  },
}));

const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': { minWidth: 200 },
});

const StyledKeyboardArrowRight = styled(KeyboardArrowRight)<{
  $expanded?: boolean;
}>(({ theme, $expanded }) => ({
  transform: $expanded ? 'rotate(90deg)' : 'none',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shorter,
  }),
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
}));

export default AttorneyMattersFilter;
